/*
  Filandra Early Access landing page.
  Header state, mobile menu, FAQ accordion and the waitlist form. No dependencies.
  Every module initialises per element and re-runs on theme-editor section reloads.
*/
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Header: hairline + translucency once the page has moved */
  function initHeader(root) {
    root.querySelectorAll('[data-fea-header]').forEach((hdr) => {
      if (hdr.dataset.feaReady) return;
      hdr.dataset.feaReady = 'true';
      const onScroll = () => hdr.classList.toggle('is-scrolled', window.scrollY > 8);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });

      const toggle = hdr.querySelector('[data-fea-menu-toggle]');
      const menu = hdr.querySelector('[data-fea-menu]');
      if (!toggle || !menu) return;
      const label = toggle.querySelector('[data-fea-menu-label]');
      const set = (open) => {
        toggle.setAttribute('aria-expanded', String(open));
        menu.dataset.open = String(open);
        if (label) label.textContent = open ? 'Menü schließen' : 'Menü öffnen';
      };
      toggle.addEventListener('click', () => set(toggle.getAttribute('aria-expanded') !== 'true'));
      menu.addEventListener('click', (e) => { if (e.target.closest('a')) set(false); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') { set(false); toggle.focus(); }
      });
    });
  }

  /* FAQ accordion */
  function initFaq(root) {
    root.querySelectorAll('[data-fea-faq]').forEach((list) => {
      if (list.dataset.feaReady) return;
      list.dataset.feaReady = 'true';
      list.addEventListener('click', (e) => {
        const btn = e.target.closest('.fea-qa__btn');
        if (!btn) return;
        const open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.dataset.open = String(open);
      });
    });
  }

  /* Waitlist form */
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const ERR_ICON = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/><path d="M8 4.75v4M8 11.1v.15" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

  function initWaitlist(root) {
    root.querySelectorAll('[data-fea-waitlist]').forEach((sec) => {
      const form = sec.querySelector('[data-fea-form]');
      if (!form || form.dataset.feaReady) return;
      form.dataset.feaReady = 'true';

      const email = form.querySelector('[data-fea-email]');
      const err = form.querySelector('[data-fea-email-err]');
      const alertBox = form.querySelector('[data-fea-alert]');
      const submit = form.querySelector('[data-fea-submit]');
      const newsletter = form.querySelector('[data-fea-newsletter]');
      const consentText = form.querySelector('[data-fea-consent-text]');
      const sent = sec.querySelector('[data-fea-sent]');
      const confirmed = sec.querySelector('[data-fea-confirmed]');
      const startedAt = Date.now();

      const message = () => {
        const v = email.value.trim();
        if (!v) return form.dataset.errEmpty;
        if (!EMAIL_RE.test(v)) return form.dataset.errInvalid;
        return '';
      };
      const showErr = (msg) => {
        if (!msg) { err.hidden = true; err.textContent = ''; email.removeAttribute('aria-invalid'); return; }
        err.innerHTML = ERR_ICON;
        const span = document.createElement('span');
        span.textContent = msg;
        err.appendChild(span);
        err.hidden = false;
        email.setAttribute('aria-invalid', 'true');
      };
      const setBusy = (busy) => {
        submit.setAttribute('aria-busy', String(busy));
        submit.disabled = busy;
        submit.innerHTML = '';
        if (busy) {
          const sp = document.createElement('span'); sp.className = 'fea-spinner'; sp.setAttribute('aria-hidden', 'true');
          submit.appendChild(sp);
        }
        const lbl = document.createElement('span');
        lbl.textContent = busy ? form.dataset.submitting : submit.dataset.label;
        submit.appendChild(lbl);
      };
      const show = (state) => {
        form.hidden = state === 'sent' || state === 'confirmed';
        if (sent) sent.hidden = state !== 'sent';
        if (confirmed) confirmed.hidden = state !== 'confirmed';
        alertBox.hidden = state !== 'error';
        if (state === 'sent' && sent) {
          const slot = sent.querySelector('[data-fea-sent-email]');
          if (slot) slot.textContent = email.value.trim();
          sent.focus();
        }
        if (state === 'confirmed' && confirmed) confirmed.focus({ preventScroll: true });
      };

      email.addEventListener('blur', () => { if (email.value.trim()) showErr(message()); });
      email.addEventListener('input', () => { if (email.getAttribute('aria-invalid') === 'true' && !message()) showErr(''); });

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const m = message();
        showErr(m);
        if (m) { email.focus(); return; }
        alertBox.hidden = true;

        const endpoint = form.dataset.endpoint;
        if (!endpoint) { show('error'); return; }

        const data = new URLSearchParams();
        data.set('email', email.value.trim());
        data.set('first_name', (form.elements.first_name.value || '').trim());
        data.set('newsletter', newsletter && newsletter.checked ? 'yes' : 'no');
        data.set('newsletter_consent_text', newsletter && newsletter.checked && consentText ? consentText.textContent.trim() : '');
        data.set('consent_version', form.dataset.consentVersion || '');
        data.set('company', form.elements.company.value || '');
        data.set('elapsed_ms', String(Date.now() - startedAt));
        data.set('page', window.location.origin + window.location.pathname);
        data.set('locale', document.documentElement.lang || 'de');

        setBusy(true);
        try {
          const res = await fetch(endpoint, { method: 'POST', body: data, mode: 'cors', credentials: 'omit' });
          let body = null;
          try { body = await res.json(); } catch (_) { body = null; }
          setBusy(false);
          show(res.ok && body && body.ok === true ? 'sent' : 'error');
        } catch (_) {
          setBusy(false);
          show('error');
        }
      });

      // Back from the confirmation link in the double opt-in email
      const params = new URLSearchParams(window.location.search);
      if (params.get('ea') === 'bestaetigt' && confirmed) {
        show('confirmed');
        sec.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    });
  }

  function init(root = document) {
    initHeader(root);
    initFaq(root);
    initWaitlist(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
